import { FC, useEffect, useState } from "react"
import { ContentState, convertToRaw, EditorState } from "draft-js"
import { Editor } from "react-draft-wysiwyg"
import draftToHtml from "draftjs-to-html"
import htmlToDraft from "html-to-draftjs"
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"
import bbApi from "../../../../api"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import { formatMessage } from "../../../utils"
import { APIError } from "../../../../types"

interface Props {
  name: string
  label?: string
  initialValue?: string
  setFieldValue: (val: string) => void
  enableImageUpload?: boolean

  autoHeight?: boolean
}

const WysiwygEditor: FC<Props> = ({
  name,
  label,
  initialValue,
  setFieldValue,
  enableImageUpload = false,
  autoHeight = false
}) => {
  const dispatch = useAppDispatch()
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false)

  const prepareDraft = (value: string) => {
    //remove <figure> tags, for some reason it's not working
    value = value.replace(/<figure[^>]*>/g, "")
    value = value.replace(/<\/figure>/g, "")

    const draft = htmlToDraft(value)
    const contentState = ContentState.createFromBlockArray(draft.contentBlocks)
    const editorState = EditorState.createWithContent(contentState)
    return editorState
  }

  const [editorState, setEditorState] = useState(
    prepareDraft(initialValue ?? "")
  )

  const onEditorStateChange = (contentState: EditorState) => {
    const rawContent = convertToRaw(contentState.getCurrentContent())
    const html = draftToHtml(rawContent)
    setFieldValue(html)
    setEditorState(contentState)
  }

  useEffect(() => {
    // Delay editor rendering to prevent auto-focus
    setTimeout(() => {
      setIsEditorReady(true)
    }, 0)
  }, [])

  return (
    <div>
      {label ? (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      ) : null}
      {isEditorReady && (
        <Editor
          editorState={editorState}
          toolbarHidden={false}
          wrapperClassName={`mt-1 rounded-md shadow-sm border border-neutral-200 ${
            autoHeight ? "" : "min-h-40"
          }`}
          editorClassName="px-4 placeholder-gray-400"
          toolbarClassName="!border-x-0 !bt-0"
          onEditorStateChange={onEditorStateChange}
          toolbar={{
            options: [
              "inline",
              ...(enableImageUpload ? ["image"] : []),
              "embedded",
              "blockType",
              "fontSize",
              "fontFamily",
              // "list",
              "textAlign",
              "colorPicker",
              // "remove",
              "history"
            ],
            inline: { inDropdown: false },
            list: { inDropdown: false },
            textAlign: { inDropdown: false },
            link: { inDropdown: false },
            history: { inDropdown: false },
            image: {
              uploadEnabled: true,
              uploadCallback: async (file: File) => {
                try {
                  const formData = new FormData()
                  formData.append("file", file)
                  const response = await bbApi.asset.uploadImage(
                    file,
                    file.name
                  )
                  if ("error" in response) throw response
                  return { data: { link: (response as { url: string }).url } }
                } catch (error) {
                  dispatch(
                    pushToast({
                      msg:
                        formatMessage((error as APIError).message) ||
                        "Failed to upload file, please try again later.",
                      timeout: 3000
                    })
                  )
                  console.error("Error uploading image:", error)
                  throw error
                }
              },
              previewImage: true,
              alt: { present: false, mandatory: false }
            },
            embedded: {
              inDropdown: false
            }
          }}
        />
      )}
    </div>
  )
}

export default WysiwygEditor
